import * as d3 from 'd3';
import { IPlayer, IPlayerConnection } from '../types/IPassingNetworkData';
import { IPlayerForVoronoi } from '../types/IVoronoiData';

const circleRadius = window.innerWidth < 800 ? 12 : 25;

export function drawPlayers(
    svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
    players: (IPlayer | IPlayerForVoronoi)[],
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    onClick: (event: any, player: IPlayer | IPlayerForVoronoi) => void // Add onClick handler as a parameter
) {
    const scatterGroup = svg.append('g').attr('id', 'scatter-group');

    scatterGroup
        .selectAll('g')
        .data(players)
        .enter()
        .append('g')
        .each(function (d, i, nodes) {
            const imageSize = window.innerWidth < 800 ? 23 : 50;

            const x = xScale(d.average_X);
            const y = yScale(d.average_Y - 2);

            // Display player circle
            d3.select(this)
                .append('circle')
                .attr('cx', x)
                .attr('cy', y)
                .attr('r', circleRadius)
                .style('fill', 'lightblue');

            // Display player image
            d3.select(this)
                .append('image')
                .attr('x', x - imageSize / 2)
                .attr('y', y - imageSize / 2)
                .attr('width', imageSize)
                .attr('height', imageSize)
                .attr('xlink:href', d.playerImage)
                .on('click', (event) => {
                    onClick(event, d);
                });

            const textPosition = window.innerWidth < 800 ? y + imageSize / 2 + 8 : y + circleRadius + 18;
            // Display player name
            d3.select(this)
                .append('text')
                .attr('x', x)
                .attr('y', textPosition)
                .attr('text-anchor', 'middle')
                .style('fill', 'white')
                .style("font-size", window.innerWidth < 800 ? "8px" : "18px")
                .style("font-weight", "Bold")
                .text(d.playerName);
        });
}
export const calculateStrokeWidthScale = (passNetworkPlayerPasses: IPlayerConnection[]) => {
    // Filter out undefined or invalid values from the passes array
    const validPasses = passNetworkPlayerPasses.map(connection => connection.passes)
        .filter(pass => typeof pass === 'number' && !isNaN(pass)) as number[];

    return d3.scaleLinear()
        .domain([0, d3.max(validPasses) || 0]) // Default to 0 if the array is empty
        .range([1, window.innerWidth < 800 ? 5 : 15]);
};

export const drawPlayerConnections = (
    svg: d3.Selection<SVGSVGElement, unknown, any, undefined>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    passNetworkPlayers: IPlayer[],
    passNetworkPlayerPasses: IPlayerConnection[],
    tooltipRef: React.RefObject<HTMLDivElement> // Add tooltipRef as an argument
) => {
    const strokeWidthScale = calculateStrokeWidthScale(passNetworkPlayerPasses);
    const tooltip = d3.select(tooltipRef.current);

    const linesGroup = svg.append('g').attr('class', 'lines-group');

    passNetworkPlayerPasses.forEach(connection => {
        const startPlayer = passNetworkPlayers.find(player => player.playerId === connection.player1);
        const endPlayer = passNetworkPlayers.find(player => player.playerId === connection.player2);
        // Check if startPlayer and endPlayer are defined
        if (startPlayer && endPlayer) {
            const strokeWidth = strokeWidthScale(connection.passes); // Using the calculated stroke width

            const line = linesGroup.append('line') // Append line to group
                .attr('x1', xScale(startPlayer.average_X))
                .attr('y1', yScale(startPlayer.average_Y)) // Adjusted to be the top of the circle
                .attr('x2', xScale(endPlayer.average_X))
                .attr('y2', yScale(endPlayer.average_Y)) // Adjusted to be the top of the circle
                .style('stroke', '#0AFC7F')
                .style('stroke-width', strokeWidth);

            // Mouseover event listener
            line.on('mouseover', function (event) {
                if (tooltipRef.current) {
                    const [x, y] = d3.pointer(event); // Get the pointer coordinates relative to the SVG container

                    // Display the tooltip with transition
                    tooltip.transition().duration(200).style('opacity', 1);
                    tooltip.html(`
                        <strong>${connection.player1Name + ': '}</strong> ${connection.player1Passes} 
                        <br/><strong>${connection.player2Name + ': '}</strong> ${connection.player2Passes} 
                        <br/><strong>${'Total Passes: '}</strong> ${connection.passes} 
                    `);
                    tooltip.style('left', `${x + 10}px`).style('top', `${y - 20}px`);
                }
            });

            // Mouseout event listener
            line.on('mouseout', () => {
                tooltip.transition().duration(200).style('opacity', 0);
            });
        } else {
            console.warn("Start player or end player not found for connection:", connection);
        }
    });
};


